import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isSubPage = location.pathname.startsWith('/use-cases');

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['home', 'services', 'clients', 'about', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  const handleNavClick = (id: string) => {
    if (id === 'usecases') {
      navigate('/use-cases');
      setMenuOpen(false);
      return;
    }
    if (isSubPage) {
      // On any /use-cases/* page: go back to main page then scroll
      navigate('/');
      setMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 64, behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const effectiveActive = isSubPage ? 'usecases' : activeSection;

  const navLinks = [
    { id: 'home',     label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'clients',  label: t('nav.clients', 'Nos Clients') },
    { id: 'usecases', label: t('nav.usecases', "Cas d'usages") },
    { id: 'about',    label: t('nav.about') },
    { id: 'contact',  label: t('nav.contact') },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: isScrolled
          ? 'rgba(10,10,15,0.85)'
          : 'transparent',
        backdropFilter: isScrolled ? 'blur(16px)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'blur(16px)' : 'none',
        borderBottom: isScrolled
          ? '1px solid rgba(255,255,255,0.08)'
          : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button onClick={() => handleNavClick('home')} className="flex items-center">
            <img src="/logo.svg" alt="Colibri Technologies" className="h-10 w-auto" />
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleNavClick(id)}
                className="text-sm font-medium transition-colors duration-200"
                style={{
                  color: effectiveActive === id
                    ? 'var(--color-primary)'
                    : 'var(--color-text-secondary)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {label}
              </button>
            ))}

            <button
              onClick={toggleLanguage}
              className="btn-ghost text-xs px-3 py-1.5"
            >
              {i18n.language === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span
              className="block w-5 h-0.5 transition-all duration-300"
              style={{
                background: 'var(--color-text-primary)',
                transform: menuOpen ? 'rotate(45deg) translate(4px,4px)' : 'none',
              }}
            />
            <span
              className="block w-5 h-0.5 transition-all duration-300"
              style={{
                background: 'var(--color-text-primary)',
                opacity: menuOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-5 h-0.5 transition-all duration-300"
              style={{
                background: 'var(--color-text-primary)',
                transform: menuOpen ? 'rotate(-45deg) translate(4px,-4px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className="md:hidden px-6 pb-6 pt-2 flex flex-col gap-4"
          style={{
            background: 'rgba(10,10,15,0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {navLinks.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => handleNavClick(id)}
              className="text-left text-sm font-medium py-1 transition-colors"
              style={{
                color: effectiveActive === id
                  ? 'var(--color-primary)'
                  : 'var(--color-text-secondary)',
                fontFamily: 'var(--font-body)',
              }}
            >
              {label}
            </button>
          ))}
          <button onClick={toggleLanguage} className="btn-ghost text-xs w-max px-3 py-1.5">
            {i18n.language === 'fr' ? 'EN' : 'FR'}
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
